/**
 * Honest protection label for a capsule (decision D-3 A, 2026-10-11).
 *
 * The web app, the browser extension and the Command Center send gzip +
 * base64 only. The only real end-to-end encryption is the CLI's
 * `collect.py send-to-hive <workspace> --encrypt`, which records
 * pqc_algorithm = "ml-kem-768+aes-256-gcm+gzip" (apps/cli/encryption.py ALG_ID).
 *
 * The Odoo model field defaults to "ML-KEM-768" (hive_identity.py) and the
 * Odoo record status is "sealed" for every capsule the bridge creates, so
 * neither proves encryption. Only the CLI envelope id does.
 *
 * Pure module so it can be unit-tested with `node --test`.
 */

export type ProtectionKind = 'encrypted' | 'compressed';

export interface Protection {
  kind: ProtectionKind;
  /** Short label for pills: "encrypted" | "compressed" */
  label: string;
  /** One-line explanation for tooltips and detail views */
  description: string;
}

const ENVELOPE_RE = /^ml-kem-768\+aes-256-gcm/i;

export function isEncrypted(pqcAlgorithm: string | null | undefined): boolean {
  return ENVELOPE_RE.test((pqcAlgorithm || '').trim());
}

export function capsuleProtection(pqcAlgorithm: string | null | undefined): Protection {
  const declared = (pqcAlgorithm || '').trim();
  if (isEncrypted(declared)) {
    return {
      kind: 'encrypted',
      label: 'encrypted',
      description: 'ML-KEM-768 + AES-256-GCM envelope (sent from the CLI with --encrypt).',
    };
  }
  return {
    kind: 'compressed',
    label: 'compressed',
    description: `gzip + SHA-256, not encrypted${declared ? ` (declared: ${declared})` : ''}. ML-KEM-768 encryption is CLI-only for now.`,
  };
}
